import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * shadcn/ui's Card, with the deck's palette in place of the default theme.
 * Same API — Card / CardHeader / CardTitle / CardDescription / CardContent /
 * CardFooter — so the composition reads the way shadcn's docs do.
 */

export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-2xl border border-line bg-white shadow-card', className)}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 p-4 pb-0 sm:p-6 sm:pb-0', className)} {...props} />
  ),
)
CardHeader.displayName = 'CardHeader'

export const CardEyebrow = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-[11px] font-bold uppercase tracking-[0.13em] text-teal', className)}
      {...props}
    />
  ),
)
CardEyebrow.displayName = 'CardEyebrow'

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn('text-[20px] font-bold leading-tight tracking-tight text-navy-950 sm:text-[22px]', className)}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-[12.5px] leading-snug text-muted', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-4 sm:p-6', className)} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-4 pt-0 sm:p-6 sm:pt-0', className)} {...props} />
  ),
)
CardFooter.displayName = 'CardFooter'

/** Soft coloured panel for notices, matching the deck's cream callouts. */
export function Note({
  tone = 'amber',
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { tone?: 'amber' | 'error' | 'green' | 'navy' }) {
  const tones = {
    amber: 'bg-amber-soft text-amber-ink',
    error: 'bg-orange-soft text-orange',
    green: 'bg-green-soft text-green',
    navy: 'bg-navy-950 text-white',
  }
  return (
    <div
      className={cn('rounded-xl px-4 py-3 text-[12.5px] leading-relaxed', tones[tone], className)}
      {...props}
    />
  )
}

export function Badge({
  tone = 'green',
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { tone?: 'green' | 'amber' | 'orange' | 'teal' }) {
  const tones = {
    green: 'bg-green-soft text-green',
    amber: 'bg-amber-soft text-amber-ink',
    orange: 'bg-orange-soft text-orange',
    teal: 'bg-teal-soft text-teal',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold',
        tones[tone],
        className,
      )}
      {...props}
    />
  )
}
